import { listFolder } from "../lib/bunnyStorage"

export interface GuidelineAsset {
  id: string
  cat: string
  type: "photo" | "video"
  url: string
}

const IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".svg", ".bmp"]
const VIDEO_EXTENSIONS = [".mp4", ".webm", ".ogg", ".mov", ".avi", ".mkv"]

const trimSlashes = (value: string) => value.replace(/^\/+|\/+$/g, "")

const ensureTrailingSlash = (value: string) => (value.endsWith("/") ? value : `${value}/`)

const encodeSegment = (value: string) => encodeURIComponent(trimSlashes(value))

const buildCdnUrl = (cdnBase: string, ...segments: string[]) =>
  [cdnBase.replace(/\/+$/, ""), ...segments.map((seg) => encodeSegment(seg))].join("/")

const getAssetType = (filename: string): "photo" | "video" | null => {
  const lowerFilename = filename.toLowerCase()
  if (VIDEO_EXTENSIONS.some((ext) => lowerFilename.endsWith(ext))) return "video"
  if (IMAGE_EXTENSIONS.some((ext) => lowerFilename.endsWith(ext))) return "photo"
  return null
}

const getFilenameWithoutExtension = (filename: string): string => {
  const lastDotIndex = filename.lastIndexOf(".")
  if (lastDotIndex === -1) return filename
  return filename.substring(0, lastDotIndex)
}

const filterDirectories = (entries: { name: string }[]) => entries.filter((entry) => !getAssetType(entry.name))

const safeListFolder = async (path: string) => {
  try {
    return await listFolder(path)
  } catch (error) {
    console.error(`[fetchGuidelineAssets] Failed to list ${path}:`, error)
    return []
  }
}

const mapFilesToAssets = (
  cdnBase: string,
  basePath: string,
  catLabel: string,
  pathSegments: string[],
  entries: BunnyEntry[],
) =>
  entries
    .map((file) => ({ file, type: getAssetType(file.name) }))
    .filter(({ type }) => type)
    .map(({ file, type }) => ({
      id: getFilenameWithoutExtension(file.name),
      cat: catLabel,
      type: type as "photo" | "video",
      url: buildCdnUrl(cdnBase, ...[basePath, ...pathSegments, file.name]),
    }))

const collectAssetsForCategory = async (
  cdnBase: string,
  basePath: string,
  categoryPath: string,
): Promise<GuidelineAsset[]> => {
  const categorySegments = trimSlashes(categoryPath)
    .split("/")
    .map((seg) => trimSlashes(seg))
    .filter(Boolean)

  const catLabel = categorySegments[categorySegments.length - 1] || categoryPath
  const categoryFullPath = `${trimSlashes(basePath)}/${categorySegments.join("/")}/`
  const entries = await safeListFolder(categoryFullPath)
  const assets: GuidelineAsset[] = []

  // Assets directly inside the category folder
  assets.push(...mapFilesToAssets(cdnBase, basePath, catLabel, categorySegments, entries))

  // Look for photos/videos subfolders if present
  const photosDir = entries.find((entry) => entry.name.toLowerCase() === "photos")
  const videosDir = entries.find((entry) => entry.name.toLowerCase() === "videos")

  if (photosDir) {
    const photosPath = `${categorySegments.join("/")}/photos/`
    const photoEntries = await safeListFolder(photosPath)
    assets.push(
      ...mapFilesToAssets(cdnBase, basePath, catLabel, [...categorySegments, "photos"], photoEntries).filter(
        (a) => a.type === "photo",
      ),
    )
  }

  if (videosDir) {
    const videosPath = `${categorySegments.join("/")}/videos/`
    const videoEntries = await safeListFolder(videosPath)
    assets.push(
      ...mapFilesToAssets(cdnBase, basePath, catLabel, [...categorySegments, "videos"], videoEntries).filter(
        (a) => a.type === "video",
      ),
    )
  }

  return assets
}

export async function fetchGuidelineAssets(basePath: string): Promise<GuidelineAsset[]> {
  const cdnBase = import.meta.env.VITE_BUNNY_CDN_BASE_URL?.replace(/\/+$/, "")

  if (!cdnBase) {
    console.error("[fetchGuidelineAssets] VITE_BUNNY_CDN_BASE_URL is not configured")
    return []
  }

  const normalizedBasePath = trimSlashes((basePath || "").replace(/^"+|"+$/g, ""))
  const rootEntries = await safeListFolder(ensureTrailingSlash(normalizedBasePath))
  const categoryFolders = filterDirectories(rootEntries)
  const allAssets: GuidelineAsset[] = []

  for (const category of categoryFolders) {
    const categoryName = trimSlashes(category.name)

    const categoryAssets = await collectAssetsForCategory(cdnBase, normalizedBasePath, categoryName)
    allAssets.push(...categoryAssets)
  }

  return allAssets
}
